import { decodeHTMLEntities } from "@/utils/common";
import Head from "next/head";
import { usePathname } from "next/navigation";
import { ReactNode } from "react";

interface HocHeadProps {
  children?: ReactNode;
  seo?: any;
  type?: "template" | null;
}

const commonMeta = [
  { name: "description", content: "metaDescription" },
  { name: "keywords", content: "keywords" },
  { name: "og:url", content: "canonicalURL" },
  { name: "og:title", content: "metaTitle" },
  { name: "og:description", content: "metaDescription" },
  { name: "twitter:url", content: "canonicalURL" },
  { name: "twitter:title", content: "metaTitle" },
  { name: "twitter:description", content: "metaDescription" },
];

const resourceUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL;

const HocHead = ({ children, seo = {}, type = null }: HocHeadProps) => {
  const pathname = usePathname();
  const canonicalURL = `https://www.trootech.com${pathname}`;

  const formatJson = (json: Record<string, any>) => ({
    __html: JSON.stringify(json, null, 2),
  });

  const imageUrl = seo.metaImage?.data?.attributes?.url
    ? `${resourceUrl}${seo.metaImage.data.attributes.url}`
    : `${resourceUrl}/images/og-image.jpg`;

  const imageWidth = seo.metaImage?.data?.attributes?.width || 1200;
  const imageHeight = seo.metaImage?.data?.attributes?.height || 630;

  return (
    <Head>
      <meta charSet="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      <title>
        {seo?.metaTitle ? decodeHTMLEntities(seo.metaTitle) : "Trootech"}
      </title>

      <link rel="icon" href="/favicon.png" type="image/png" />
      <link rel="mask-icon" href="/favicon.ico" color="#5bbad5" />

      {type === "template" && (
        <>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
        </>
      )}

      <meta property="og:type" content="website" />
      <meta property="twitter:card" content="summary_large_image" />

      <link rel="canonical" href={seo.canonicalURL || canonicalURL} />

      {seo.structuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={formatJson(seo.structuredData)}
        />
      )}

      <meta property="twitter:image" content={imageUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content={String(imageWidth)} />
      <meta property="og:image:height" content={String(imageHeight)} />

      <meta
        name="title"
        property="og:title"
        content={`${
          seo?.metaTitle
            ? decodeHTMLEntities(seo?.metaTitle)
            : "AI Solutions, Enterprise Platforms, Data Engineering & Custom Software"
        } - TRooTech`}
      />

      {commonMeta.map(({ name, content }) =>
        seo?.[content] ? (
          <meta key={name} name={name} content={seo[content] as string} />
        ) : null
      )}

      {children}
    </Head>
  );
};

export default HocHead;
